/**
 * Virtual computer QWERTY → MIDI note mapping.
 * Uses KeyboardEvent.code (layout-stable) and capture-phase listeners
 * so note-on fires as early as possible on laptop keyboards.
 */

export interface QwertyMapOptions {
  baseOctave?: number;
  shiftOctaveUp?: boolean;
}

export interface QwertyNoteEvent {
  type: "noteon" | "noteoff";
  midiNote: number;
  velocity: number;
  key: string;
  code: string;
}

export type QwertyNoteHandler = (event: QwertyNoteEvent) => void;

/** Physical key codes → white-key index from C. */
const WHITE_CODES: Record<string, number> = {
  KeyA: 0,
  KeyS: 1,
  KeyD: 2,
  KeyF: 3,
  KeyG: 4,
  KeyH: 5,
  KeyJ: 6,
  KeyK: 7,
  KeyL: 8,
  Semicolon: 9,
};

/** Physical key codes → white index whose sharp is one semitone up. */
const BLACK_CODES: Record<string, number> = {
  KeyW: 0,
  KeyE: 1,
  KeyT: 3,
  KeyY: 4,
  KeyU: 5,
  KeyO: 7,
  KeyP: 8,
};

const WHITE_OFFSETS = [0, 2, 4, 5, 7, 9, 11, 12, 14, 16];

export type AudioSink = (
  type: "noteon" | "noteoff",
  midiNote: number,
  velocity: number,
) => void;

export class QwertyKeyboardMapper {
  private handlers = new Set<QwertyNoteHandler>();
  /** Called FIRST on mapped keys — wire directly to synth for lowest latency. */
  private audioSink: AudioSink | null = null;
  /** code → midi currently held */
  private held = new Map<string, number>();
  private baseOctave: number;
  private shiftOctaveUp: boolean;
  private enabled = true;
  private attached = false;
  private target: Window | Document | null = null;

  private onKeyDown = (e: Event): void => this.handleKey(e as KeyboardEvent, true);
  private onKeyUp = (e: Event): void => this.handleKey(e as KeyboardEvent, false);

  constructor(options: QwertyMapOptions = {}) {
    this.baseOctave = options.baseOctave ?? 4;
    this.shiftOctaveUp = options.shiftOctaveUp ?? true;
  }

  /** Direct synth hook — invoked before any UI/logic handlers. */
  setAudioSink(sink: AudioSink | null): void {
    this.audioSink = sink;
  }

  onNote(handler: QwertyNoteHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.releaseAll();
  }

  setBaseOctave(octave: number): void {
    this.baseOctave = Math.max(1, Math.min(7, octave));
  }

  attach(target: Window | Document = window): void {
    if (this.attached) return;
    this.target = target;
    // Capture phase = earliest possible delivery before React/DOM handlers.
    target.addEventListener("keydown", this.onKeyDown, { capture: true });
    target.addEventListener("keyup", this.onKeyUp, { capture: true });
    this.attached = true;
  }

  detach(target: Window | Document = window): void {
    if (!this.attached) return;
    const t = this.target ?? target;
    t.removeEventListener("keydown", this.onKeyDown, true);
    t.removeEventListener("keyup", this.onKeyUp, true);
    this.attached = false;
    this.target = null;
    this.releaseAll();
  }

  resolveMidiFromCode(code: string, shiftKey: boolean): number | null {
    const baseC = (this.baseOctave + 1) * 12;
    const bump = this.shiftOctaveUp && shiftKey ? 12 : 0;

    if (code in WHITE_CODES) {
      return baseC + WHITE_OFFSETS[WHITE_CODES[code]] + bump;
    }
    if (code in BLACK_CODES) {
      return baseC + WHITE_OFFSETS[BLACK_CODES[code]] + 1 + bump;
    }
    return null;
  }

  /** @deprecated prefer resolveMidiFromCode */
  resolveMidi(key: string, shiftKey: boolean): number | null {
    const fake = key.length === 1 ? `Key${key.toUpperCase()}` : key;
    if (key === ";") return this.resolveMidiFromCode("Semicolon", shiftKey);
    return this.resolveMidiFromCode(fake, shiftKey);
  }

  getLayoutHelp(
    includeShift = false,
  ): Array<{ key: string; note: string; midi: number }> {
    const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    const labels: Array<[string, string]> = [
      ["A", "KeyA"],
      ["S", "KeyS"],
      ["D", "KeyD"],
      ["F", "KeyF"],
      ["G", "KeyG"],
      ["H", "KeyH"],
      ["J", "KeyJ"],
      ["K", "KeyK"],
      ["L", "KeyL"],
      [";", "Semicolon"],
      ["W", "KeyW"],
      ["E", "KeyE"],
      ["T", "KeyT"],
      ["Y", "KeyY"],
      ["U", "KeyU"],
      ["O", "KeyO"],
      ["P", "KeyP"],
    ];
    const base = labels
      .map(([key, code]) => {
        const midi = this.resolveMidiFromCode(code, false)!;
        const name = names[midi % 12];
        const oct = Math.floor(midi / 12) - 1;
        return { key, note: `${name}${oct}`, midi };
      });
    const shifted = includeShift
      ? labels.map(([key, code]) => {
          const midi = this.resolveMidiFromCode(code, true)!;
          const name = names[midi % 12];
          const oct = Math.floor(midi / 12) - 1;
          return { key: `⇧${key}`, note: `${name}${oct}`, midi };
        })
      : [];
    return [...base, ...shifted].sort((a, b) => a.midi - b.midi);
  }

  private handleKey(e: KeyboardEvent, isDown: boolean): void {
    if (!this.enabled) return;
    if (e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target as HTMLElement | null;
    if (target && this.shouldIgnoreTarget(target)) {
      return;
    }

    if (isDown) {
      if (e.repeat) return;
      if (this.held.has(e.code)) return;

      const midi = this.resolveMidiFromCode(e.code, e.shiftKey);
      if (midi == null) return;

      this.held.set(e.code, midi);
      e.preventDefault();
      this.emit({
        type: "noteon",
        midiNote: midi,
        velocity: 100,
        key: e.key,
        code: e.code,
      });
      return;
    }

    const midi = this.held.get(e.code);
    if (midi == null) return;
    this.held.delete(e.code);
    e.preventDefault();
    this.emit({
      type: "noteoff",
      midiNote: midi,
      velocity: 0,
      key: e.key,
      code: e.code,
    });
  }

  private releaseAll(): void {
    for (const [code, midi] of this.held) {
      this.emit({
        type: "noteoff",
        midiNote: midi,
        velocity: 0,
        key: "",
        code,
      });
    }
    this.held.clear();
  }

  /** Text fields steal keys; range/checkbox must not (volume while playing). */
  private shouldIgnoreTarget(target: HTMLElement): boolean {
    if (target.isContentEditable) return true;
    const tag = target.tagName;
    if (tag === "TEXTAREA" || tag === "SELECT") return true;
    if (tag !== "INPUT") return false;
    const type = ((target as HTMLInputElement).type || "text").toLowerCase();
    return type !== "range" && type !== "checkbox" && type !== "radio" && type !== "button";
  }

  private emit(event: QwertyNoteEvent): void {
    // Audio BEFORE any UI / grading handlers.
    if (this.audioSink) {
      this.audioSink(event.type, event.midiNote, event.velocity);
    }
    for (const h of this.handlers) h(event);
  }
}
